/**
 * A tiny inline-markup helper for the one field that carries formatting: a pick's
 * editor's note (`picks[p].marginalia`). The page renders it as HTML, and the Content
 * API keeps only inline `strong`, `em` and links (`b` and `i` become `strong` and `em`;
 * link targets must be http, https, mailto, tel or relative). No rich-text library:
 * the field is a textarea holding that inline HTML, the toolbar wraps the selection,
 * and `safeInlineHtml` renders a preview with the same allowlist.
 */

export interface TextEdit {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

export function wrapSelection(
  value: string,
  start: number,
  end: number,
  open: string,
  close: string,
  placeholder: string
): TextEdit {
  const inner = value.slice(start, end) || placeholder;
  const next = `${value.slice(0, start)}${open}${inner}${close}${value.slice(end)}`;
  return {
    value: next,
    selectionStart: start + open.length,
    selectionEnd: start + open.length + inner.length,
  };
}

export const bold = (v: string, s: number, e: number) => wrapSelection(v, s, e, '<strong>', '</strong>', 'bold');
export const italic = (v: string, s: number, e: number) => wrapSelection(v, s, e, '<em>', '</em>', 'italic');

/** http, https, mailto, tel or a relative link: what the Content API accepts. */
export function isSafeHref(href: string): boolean {
  const h = href.trim();
  if (!h || /[\s"<>]/.test(h)) return false;
  if (/^(https?:|mailto:|tel:)/i.test(h)) return true;
  // Relative: no scheme at all (so `javascript:` and friends never pass).
  return !/^[a-z][a-z0-9+.-]*:/i.test(h) && !h.startsWith('//');
}

export function link(value: string, start: number, end: number, href: string): TextEdit {
  return wrapSelection(value, start, end, `<a href="${href.trim()}">`, '</a>', 'link text');
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * The note as the page would show it: inline strong / em / safe links only, every
 * other tag dropped (its text kept), everything escaped. Safe for innerHTML.
 */
export function safeInlineHtml(html: string): string {
  if (typeof DOMParser === 'undefined') return escapeHtml(html);
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return escapeHtml(node.textContent ?? '');
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const el = node as Element;
    const inner = Array.from(el.childNodes).map(walk).join('');
    switch (el.tagName.toLowerCase()) {
      case 'strong':
      case 'b':
        return `<strong>${inner}</strong>`;
      case 'em':
      case 'i':
        return `<em>${inner}</em>`;
      case 'a': {
        const href = el.getAttribute('href') ?? '';
        return isSafeHref(href)
          ? `<a href="${escapeHtml(href.trim())}" target="_blank" rel="noopener noreferrer">${inner}</a>`
          : inner;
      }
      case 'script':
      case 'style':
        return '';
      default:
        return inner;
    }
  };
  return Array.from(doc.body.childNodes).map(walk).join('');
}
